/**
 * The world garden: the globe of published gardens, and the account screens
 * that publish to it.
 *
 * The globe (`src/globe/`) and its content (`src/content/globe.ts`) are both
 * dynamic imports: the baked world grid and the renderer cost nothing until
 * this opens. Reading the map loads no Firebase code and stores nothing on the
 * device — see `src/world/publicRead.ts`.
 *
 * **This module is imported statically by `main.ts`, so it must only ever
 * reach the globe, the account UI and Firebase through `import()`.** Its own
 * static imports are types and `world/gate.ts`, which is free of imports by
 * design. After a build, `dist/index.html` must still reference only `index`,
 * `three` and the stylesheet.
 */

import type { WorldGarden } from '../content/globe';
import type { AudioEngine } from '../core/AudioEngine';
import type { GlobeHandle } from '../globe/open';
import type { UI } from '../ui/UI';
import type { WorldAccountController } from '../ui/worldAccount';
import { accountMode } from '../world/gate';

/** What a publish would put on the map, or null when there is nothing of ours to publish. */
export interface PublishableGarden {
  treesGrown: number;
  tier: number;
  seed: string;
}

export interface WorldHost {
  readonly ui: UI;
  readonly audio: AudioEngine;
  /** Null during a visit: a borrowed garden is not the player's to publish. */
  publishable(): PublishableGarden | null;
  /** True inside an immersive WebXR session. */
  immersive(): boolean;
}

export class WorldMap {
  private host: WorldHost;
  /** Created on first open and kept; see `src/globe/open.ts`. */
  private globe: GlobeHandle | null = null;
  /**
   * The world map's account screens, created on the first globe open that is
   * allowed them and kept. Always null in a production build while
   * `WORLD_PUBLISHING_ENABLED` is false — see `src/world/gate.ts`.
   */
  private worldAccount: WorldAccountController | null = null;
  /*
   * The mount in flight. Two quick taps on the globe button run `show`
   * twice, and checking `worldAccount` alone let both past the `await` and
   * mounted two panels, one of them orphaned over the globe.
   */
  private worldAccountLoading: Promise<WorldAccountController | null> | null = null;
  /** Re-reads the globe for the open that is current. */
  private worldRefresh: ((focus?: { lat: number; lng: number }) => Promise<void>) | null = null;

  constructor(host: WorldHost) {
    this.host = host;
  }

  get isOpen(): boolean {
    return this.globe?.isOpen ?? false;
  }

  async show(): Promise<void> {
    const { ui, audio } = this.host;
    audio.tick();
    const root = document.getElementById('globe-root');
    if (!root) return;

    let open: typeof import('../globe/open');
    let content: typeof import('../content/globe');
    try {
      [open, content] = await Promise.all([import('../globe/open'), import('../content/globe')]);
    } catch {
      ui.toast('World view unavailable', 'It could not be loaded. Check your connection.');
      return;
    }

    const gardens = new Map<string, WorldGarden>();
    let myUid: string | null = null;
    this.globe = open.openGlobe(
      root,
      content.GLOBE_THEME,
      content.worldModel([], null, content.GLOBE_COPY.loading, this.worldAccount?.actions),
      {
        describe: (cluster) => content.describeCluster(cluster, gardens, myUid),
        onClose: () => {
          this.worldAccount?.close();
          this.globe?.close();
        },
        onAction: (id) => {
          this.worldAccount?.handleAction(id);
        },
      },
    );

    const refresh = async (focus?: { lat: number; lng: number }): Promise<void> => {
      const loaded = await this.loadWorldGardens(content.GLOBE_COPY);
      const account = this.worldAccount;
      const mine = account?.mine ?? null;
      // The account's own garden is merged in rather than trusted to the
      // public read: it is the fresher copy straight after a publish, and on
      // the dev server's fake account it is the only copy there is.
      const list = mine ? [mine, ...loaded.list.filter((g) => g.uid !== mine.uid)] : loaded.list;
      myUid = account?.uid ?? loaded.uid;
      gardens.clear();
      for (const garden of list) gardens.set(garden.uid, garden);
      if (!this.globe?.isOpen) return;
      this.globe.update(content.worldModel(list, myUid, loaded.status, account?.actions));
      if (focus) this.globe.focus(focus.lat, focus.lng, 1.7);
    };
    this.worldRefresh = refresh;

    this.worldAccountLoading ??= this.mountWorldAccount(root);
    this.worldAccount = await this.worldAccountLoading;
    await this.worldAccount?.restore();
    await refresh();
  }

  /**
   * Load the account screens and the account behind them, if allowed.
   *
   * **The first line is what keeps them out of the public build.** It reads
   * the flag as `import.meta.env.WORLD_PUBLISHING_ENABLED`, which
   * `vite.config.ts` defines from `WORLD_PUBLISHING_ENABLED` in
   * `src/content/client.ts`. A define is replaced with a literal before the
   * bundler builds its module graph, so a production build with the flag off
   * sees `if (!(false || false)) return null` and never emits the chunks
   * behind it — the wording, the styles, the adapter, or the Firebase SDK.
   * Importing the constant from `client.ts` instead does *not* work: it is
   * only folded at minification, after the chunks are already written. The
   * fake account's import has its own `import.meta.env.DEV` for when the
   * flag is on.
   *
   * Importing the Firebase adapter starts nothing: the SDK only initialises
   * when the player signs in, or when this device has signed in before.
   */
  private async mountWorldAccount(root: HTMLElement): Promise<WorldAccountController | null> {
    if (!(import.meta.env.DEV || import.meta.env.WORLD_PUBLISHING_ENABLED)) return null;
    const mode = accountMode({
      enabled: import.meta.env.WORLD_PUBLISHING_ENABLED,
      dev: import.meta.env.DEV,
      search: location.search,
    });
    if (mode === 'off') return null;
    try {
      const [{ mountWorldAccount }, account] = await Promise.all([
        import('../ui/worldAccount'),
        import.meta.env.DEV && mode === 'fake'
          ? import('../world/fakeAccount').then((m) => m.fakeAccount(location.search))
          : import('../world/account').then((m) => m.loadFirebaseAccount()),
      ]);
      return mountWorldAccount(
        {
          root,
          globe: () => this.globe,
          garden: () => this.host.publishable(),
          immersive: () => this.host.immersive(),
          refresh: (focus) => this.worldRefresh?.(focus) ?? Promise.resolve(),
        },
        account,
      );
    } catch {
      // The map still works without the account screens.
      return null;
    }
  }

  /**
   * Published gardens, or an honest empty map.
   *
   * Until the Firestore database exists every read fails, and that must read
   * as "not live yet", never as an error the player caused. On the dev server
   * only, `?globe=demo` swaps in invented gardens so the globe can be looked
   * at — the branch is folded away in a production build, so no fabricated
   * garden can ever reach the public map.
   */
  private async loadWorldGardens(copy: { offline: string; demo: string }): Promise<{
    list: WorldGarden[];
    uid: string | null;
    status: string | null;
  }> {
    if (import.meta.env.DEV && new URLSearchParams(location.search).get('globe') === 'demo') {
      const { demoGardens } = await import('../content/globeDemo');
      return { list: demoGardens(), uid: null, status: copy.demo };
    }
    try {
      // The REST read, not the SDK: see `src/world/publicRead.ts` for why
      // looking at the map must not start Firebase on the viewer's device.
      const { fetchPublicGardens } = await import('../world/publicRead');
      const list = await fetchPublicGardens();
      // Whose garden is whose comes from the account screens, merged in by
      // the caller; a public read knows nobody.
      return { list, uid: null, status: null };
    } catch {
      return { list: [], uid: null, status: copy.offline };
    }
  }
}
